import { useMemo } from 'react'
import dayjs from 'dayjs'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'

export function safeNumber (value) {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

export function safeDate (value) {
  if (value === null || value === undefined || value === '') return null
  const d = dayjs(value)
  return d.isValid() ? d.valueOf() : null
}

export function numericSortFn (rowA, rowB, columnId) {
  const a = safeNumber(rowA.getValue(columnId))
  const b = safeNumber(rowB.getValue(columnId))
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a - b
}

export function dateSortFn (rowA, rowB, columnId) {
  const a = safeDate(rowA.getValue(columnId))
  const b = safeDate(rowB.getValue(columnId))
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a - b
}

const SORT_AS_FN_MAP = {
  numeric: numericSortFn,
  text: 'alphanumeric', // TanStack built-in: splits on numeric groups for natural ordering
  date: dateSortFn
}

/**
 * Maps blueprint presentation columns to tanstack react-table column defs.
 * Shared by both BlueprintDataGrid and BlueprintPresentationTable.
 */
export default function useBlueprintColumnDefs (columns, footerData, footerColumnMapping) {
  const { formatter } = useFormattingContext()

  return useMemo(() => {
    if (!columns) return { columns: [], includeFooter: false }

    const includeFooter = footerData?.length > 0 && footerColumnMapping?.length > 0
    const footerRow = includeFooter ? footerData[0] : null

    const mapped = columns
      .filter(col => !col.exclude)
      .map(col => {
        const def = {
          accessorKey: col.name,
          header: col.displayName || col.name,
          align: col.align || 'left',
          defaultShown: !col.hidden,
          enableSorting: col.sortable ?? true,
          enableColumnFilter: col.filterable ?? true,
          size: col.width || 150
        }

        if (col.sortAs && SORT_AS_FN_MAP[col.sortAs]) {
          def.sortingFn = SORT_AS_FN_MAP[col.sortAs]
        }

        if (col.formatString) {
          def.cell = (props) => formatter(props.getValue(), col.formatString)
        }

        if (includeFooter) {
          const mapping = footerColumnMapping.find(m => m.mainColumn === col.name)
          if (mapping && footerRow) {
            def.footer = () => {
              const value = footerRow[mapping.footerColumn]
              return col.formatString ? formatter(value, col.formatString) : value
            }
          }
        }

        return def
      })

    return { columns: mapped, includeFooter }
  }, [columns, footerData, footerColumnMapping, formatter])
}

/**
 * Maps blueprint defaultSort `[{ field, dir }]` to tanstack
 * react-table sort format `[{ id, desc }]`.
 */
export function mapDefaultSort (defaultSort) {
  if (!defaultSort?.length) return []
  return defaultSort.map(s => ({
    id: s.field,
    desc: s.dir === 'desc'
  }))
}
